<template>
	<div class="page" v-if="page">
		<section class="section quick-stats" v-if="page.quick_stats">
			<div class="container is-fullhd">
				<div class="columns is-multiline is-hidden-mobile">
					<div class="column" v-for="award in awards">
						<QuickStatBox :name="award.name" :icon="award.icon" :emoji="award.emoji" :value="award.value" :description="award.description" />
					</div>
				</div>

				<div class="is-hidden-tablet">
					<v-touch
						@swiperight="prevMobileAward"
						@swipeleft="nextMobileAward"
						:swipe-options="{ direction: 'horizontal' }"
						class="awards slider"
					>
						<QuickStatBox
							v-for="(award, index) in awards"
							:name="award.name"
							:icon="award.icon"
							:emoji="award.emoji"
							:value="award.value"
							:description="award.description"
							:class="{
								'is-prev': index < mobile_award_index,
								'is-active': index == mobile_award_index,
								'is-next': index > mobile_award_index,
							}"
						/>
					</v-touch>

					<nav class="pagination pagination-dots" role="navigation" aria-label="navigation">
						<ul class="pagination-list">
							<li>
								<a
									v-for="(award, index) in awards"
									class="pagination-link"
									:class="{ 'is-current': mobile_award_index == index }"
									@click="setMobileAward(index)"
									>{{ award.name }}</a
								>
							</li>
						</ul>
					</nav>
				</div>
			</div>
		</section>

		<section class="section">
			<div class="container content is-fullhd">
				<div class="columns" v-if="page.last_week || page.next_week">
					<div class="column is-half" v-if="page.last_week">
						<Matchups :week_id="page.last_week.week_id" :matchups="page.last_week.matchups" :is_complete="true" />

						<div class="cta-box has-text-centered" v-if="page.uses_playoffs">
							<router-link
								class="button is-medium is-rounded is-link is-outlined"
								:to="{ name: 'league-playoffs', params: { leagueKey: league.key } }"
								>{{ page.are_playoffs_active ? "View Playoff Bracket" : "View Playoff Race" }}</router-link
							>
						</div>
					</div>

					<div class="column is-half" v-if="page.next_week">
						<Matchups :week_id="page.next_week.week_id" :matchups="page.next_week.matchups" :is_complete="false" />

						<div class="cta-box has-text-centered" v-if="!page.last_week && page.uses_playoffs">
							<router-link
								class="button is-medium is-rounded is-link is-outlined"
								:to="{ name: 'league-playoffs', params: { leagueKey: league.key } }"
								>{{ page.are_playoffs_active ? "View Playoff Bracket" : "View Playoff Race" }}</router-link
							>
						</div>
					</div>

					<div class="column is-half" v-if="page.league_winner">
						<div class="league-winner has-text-centered">
							<div class="icon is-medium">
								<font-awesome-icon :icon="['fas', 'trophy']" class="fa-4x" />
							</div>

							<div class="is-size-2"><TeamLink :teamKey="page.league_winner.key" :teamName="page.league_winner.name" /></div>

							<div class="is-size-3">{{ page.league_winner.record }}</div>

							<div class="level is-mobile">
								<div class="level-item is-narrow is-size-4">
									{{ page.league_winner.rank_average_points_for }}<br />
									Points
								</div>
								<div class="level-item is-narrow is-size-4">
									{{ page.league_winner.rank_luck }}<br />
									Luck
								</div>
								<div class="level-item is-narrow is-size-4">
									{{ page.league_winner.rank_strength_of_schedule }}<br />
									SOS
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>

		<section class="section is-hidden-mobile">
			<div class="container content is-fullhd">
				<h2 class="has-text-centered">Weekly Performance Ratings</h2>

				<Chart :config="charts.weekly_performances" />
			</div>
		</section>

		<section class="section">
			<div class="container content is-fullhd">
				<h2 class="has-text-centered">Standings</h2>

				<div class="columns is-multiline" v-if="page.divisions && page.divisions.length">
					<div class="column is-full" v-for="division in page.divisions">
						<Standings :heading="division.name" :teams="division.teams" />
					</div>
				</div>

				<Standings v-else-if="page.standings" :teams="page.standings" />
			</div>
		</section>

		<section class="section is-hidden-mobile">
			<div class="container content is-fullhd">
				<h2 class="has-text-centered">Team Comparisons</h2>

				<Chart :config="charts.standings" />
			</div>
		</section>

		<section class="section">
			<div class="container content is-fullhd">
				<div class="columns" v-if="page.biggest_blowouts && page.biggest_blowouts.length">
					<div class="column">
						<Matchups heading="Biggest Blowouts" :matchups="page.biggest_blowouts" :is_complete="true" :limit_mobile="true" />
					</div>

					<div class="column">
						<Matchups heading="Closest Matchups" :matchups="page.closest_matchups" :is_complete="true" :limit_mobile="true" />
					</div>
				</div>
			</div>
		</section>

		<section class="section">
			<div class="container content is-fullhd">
				<div class="box">
					<h2>Worst Chosen Lineups</h2>

					<p>This table shows teams that lost their matchup but could have won if they had played a better lineup.</p>

					<table class="table is-fullwidth is-hidden-mobile">
						<thead>
							<tr>
								<th>Team</th>
								<th>Actual</th>
								<th>Possible</th>
								<th>Diff</th>
								<th>Week</th>
								<th>Opponent</th>
								<th>Opp. Points</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="matchup in page.worst_chosen_lineups">
								<td><TeamLink :teamKey="matchup.team_key" :teamName="matchup.team_name" /></td>
								<td>{{ matchup.points_scored }}</td>
								<td>{{ matchup.points_best }}</td>
								<td>{{ matchup.points_diff }}</td>
								<td>{{ matchup.week }}</td>
								<td><TeamLink :teamKey="matchup.opponent_key" :teamName="matchup.opponent_name" /></td>
								<td>{{ matchup.opponent_points }}</td>
							</tr>
						</tbody>
					</table>

					<table class="table is-fullwidth is-hidden-tablet">
						<thead>
							<tr>
								<th>Team</th>
								<th class="has-text-right">Bench</th>
							</tr>
						</thead>
						<tbody>
							<template v-for="matchup in mobile_worst_chosen_lineups">
								<tr class="has-next-row">
									<td><TeamLink :teamKey="matchup.team_key" :teamName="matchup.team_name" /></td>
									<td class="has-text-right">{{ matchup.points_diff }}</td>
								</tr>
								<tr>
									<td>vs <TeamLink :teamKey="matchup.opponent_key" :teamName="matchup.opponent_name" /></td>
									<td class="has-text-right">Week {{ matchup.week }}</td>
								</tr>
							</template>
						</tbody>
					</table>
				</div>

				<div class="cta-box has-text-centered is-hidden-tablet">
					<a href="#" class="button is-medium is-rounded is-link is-outlined" @click.prevent="showMoreOnMobile">View more</a>
				</div>
			</div>
		</section>

		<section class="section">
			<div class="container content is-fullhd">
				<div class="columns" v-if="page.high_scores && page.high_scores.length">
					<div class="column">
						<div class="is-flex is-flex-apart">
							<h2>{{ is_team_high_scores ? "Team" : "Overall" }} High Scores</h2>

							<button class="button is-rounded is-link" @click.prevent="toggleHighScores">{{ is_team_high_scores ? "Show Overall" : "Show Teams" }}</button>
						</diV>
						
						<div class="box">
							<table class="table is-fullwidth">
								<thead>
									<tr>
										<th>#</th>
										<th>Team</th>
										<th>Points</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(score, index) in (is_team_high_scores ? page.team_high_scores : page.high_scores)">
										<td>{{ index + 1 }}.</td>
										<td><TeamLink :teamKey="score.team_key" :teamName="score.team_name" /></td>
										<td>{{ score.points }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<div class="column">
						<div class="is-flex is-flex-apart">
							<h2>{{ is_team_low_scores ? "Team" : "Overall" }} Low Scores</h2>

							<button class="button is-rounded is-link" @click.prevent="toggleLowScores">{{ is_team_low_scores ? "Show Overall" : "Show Teams" }}</button>
						</div>

						<div class="box">
							<table class="table is-fullwidth">
								<thead>
									<tr>
										<th>#</th>
										<th>Team</th>
										<th>Points</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(score, index) in (is_team_low_scores ? page.team_low_scores : page.low_scores)">
										<td>{{ index + 1 }}.</td>
										<td><TeamLink :teamKey="score.team_key" :teamName="score.team_name" /></td>
										<td>{{ score.points }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import QuickStatBox from "@/components/QuickStatBox"
import Matchups from "@/components/Matchups"
import TeamLink from "@/components/TeamLink"
import Standings from "@/components/Standings"
import Chart from "@/components/Chart"

const LIMIT_MOBILE_LINEUPS_COUNT = 3

export default {
	components: {
		QuickStatBox,
		Matchups,
		TeamLink,
		Standings,
		Chart,
	},
	data() {
		return {
			mobile_award_index: 0,
			is_mobile_shown_more: false,
			is_team_high_scores: true,
			is_team_low_scores: true,
		}
	},
	computed: {
		league() {
			return this.$store.getters.activeLeague
		},
		page() {
			return this.$store.getters.activePage
		},
		awards() {
			const awards = []

			if (this.page) {
				awards.push({
					name: "Average Points",
					emoji: "&#x1F4C8;",
					...this.page.quick_stats.average_points,
				})

				awards.push({
					name: "Biggest Blowout",
					emoji: "&#x1F4A3;",
					...this.page.quick_stats.biggest_blowout,
				})

				awards.push({
					name: "Closest Matchup",
					emoji: "&#x1F90F;",
					...this.page.quick_stats.closest_matchup,
				})

				awards.push({
					name: "Worst Luck",
					emoji: "&#x1F92C;",
					...this.page.quick_stats.worst_luck,
				})

				awards.push({
					name: "Bench Warmer",
					emoji: "&#x1F6CC;",
					...this.page.quick_stats.bench_warmer,
				})

				awards.push({
					name: "High Score",
					//emoji: "&#x1F525;", // fire
					emoji: "&#x1F680;",
					...this.page.quick_stats.high_score,
				})

				awards.push({
					name: "Low Score",
					emoji: "&#x1F4A9",
					...this.page.quick_stats.low_score,
				})
			}

			return awards
		},
		charts() {
			if (!this.page) return {}

			return {
				weekly_performances: {
					type: "heatmap",
					series: this.page.graph_datas.weekly_performances.series,
					height: 400,
					options: {
						chart: {
							background: "transparent",
						},
						xaxis: {
							categories: this.page.graph_datas.weekly_performances.labels,
						},
						dataLabels: {
							enabled: true,
							formatter: (value) => (value == null ? "" : Math.floor(value) + "%"),
							style: {
								colors: ["#000000"],
							},
						},
						grid: {
							show: true,
							xaxis: {
								lines: {
									show: true,
								},
							},
							yaxis: {
								lines: {
									show: true,
								},
							},
						},
						tooltip: {
							enabled: false,
						},
						states: {
							hover: {
								filter: { type: "none" },
							},
							active: {
								filter: { type: "none" },
							},
						},
						colors: ["#0083B0"],
					},
				},
				standings: {
					type: "bar",
					series: this.page.graph_datas.standings.series,
					height: 400,
					options: {
						plotOptions: {
							bar: {
								horizontal: false,
								endingShape: "rounded",
								columnWidth: "55%",
							},
						},
						dataLabels: {
							enabled: false,
						},
						stroke: {
							show: true,
							width: 2,
							colors: ["transparent"],
						},
						xaxis: {
							categories: this.page.graph_datas.standings.labels,
						},
						yaxis: {
							decimalsInFloat: 0,
							min: 0,
							max: 100,
							tickAmount: 10,
						},
						fill: {
							opacity: 1,
						},
					},
				},
			}
		},
		is_mobile_limited() {
			return !this.is_mobile_shown_more && this.page.worst_chosen_lineups.length > LIMIT_MOBILE_LINEUPS_COUNT
		},
		mobile_worst_chosen_lineups() {
			return this.is_mobile_limited
				? this.page.worst_chosen_lineups.slice(0, LIMIT_MOBILE_LINEUPS_COUNT)
				: this.page.worst_chosen_lineups
		},
	},
	methods: {
		setMobileAward(index) {
			this.mobile_award_index = index
		},
		prevMobileAward() {
			if (this.mobile_award_index == 0) {
				this.mobile_award_index = this.awards.length - 1
			} else {
				this.mobile_award_index -= 1
			}
		},
		nextMobileAward() {
			if (this.mobile_award_index + 1 >= this.awards.length) {
				this.mobile_award_index = 0
			} else {
				this.mobile_award_index += 1
			}
		},
		showMoreOnMobile() {
			this.is_mobile_shown_more = true
		},
		toggleHighScores() {
			this.is_team_high_scores = !this.is_team_high_scores
		},
		toggleLowScores() {
			this.is_team_low_scores = !this.is_team_low_scores
		}
	},
	mounted() {
		this.$store.dispatch("setActivePage", "dashboard")
	},
}
</script>

<style scoped>
.slider.awards {
	height: 16rem;
}

.slider.awards > * {
	left: 1rem;
	width: calc(100% - 2rem);
}

.slider.awards > .is-prev {
	transform: translateX(-120%);
}

.slider.awards > .is-next {
	transform: translateX(120%);
}

.league-winner .fa-trophy {
	color: #ffc100;
}

.league-winner .level {
	justify-content: center;
}

.league-winner .level .level-item {
	margin: 0 2rem;
}
</style>
